import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeStyleTags } from "../../../../../../core/common-service/CommonService";
import "./BookPreviewModal.css";

const BookPreviewModal = ({
  isOpen,
  onClose,
  bookTitle,
  introduction,
  synopsis,
  summary,
  chapters = [],
  coverPhoto = null,
  chapterPhotos = {},
  isPaid = false,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const contentRef = useRef(null);

  // A5 dimensions in pixels at 96 DPI
  const A5_WIDTH = 559; // 14.8cm
  const A5_HEIGHT = 794; // 21cm
  const PAGE_PADDING = 80; // Combined top and bottom padding
  const AVAILABLE_HEIGHT = A5_HEIGHT - PAGE_PADDING;

  // Estimate lines per page based on line height
  const LINE_HEIGHT = 28.8; // 1.8 * 16px font size
  const LINES_PER_PAGE = Math.floor(AVAILABLE_HEIGHT / LINE_HEIGHT);

  // Max table-of-contents entries that fit on a single A5 page (accounts for the
  // TOC title plus per-entry padding/gap). Larger books spill onto extra pages.
  const ENTRIES_PER_TOC_PAGE = 12;

  // More accurate character estimation for different screen sizes
  const getCharsPerLine = () => {
    if (window.innerWidth <= 480) return 40;
    if (window.innerWidth <= 768) return 50;
    return 65;
  };

  // Split a long paragraph into smaller chunks
  const splitLongParagraph = (paragraph, charsPerLine) => {
    const maxCharsPerChunk = charsPerLine * 1;

    if (paragraph.length <= maxCharsPerChunk) {
      return [paragraph];
    }

    const chunks = [];
    const sentences = paragraph.match(/[^.!?]+[.!?]+/g) || [paragraph];
    let currentChunk = "";

    sentences.forEach((sentence) => {
      const trimmedSentence = sentence.trim();

      if (
        currentChunk &&
        (currentChunk + " " + trimmedSentence).length > maxCharsPerChunk
      ) {
        if (currentChunk.length > maxCharsPerChunk * 0.4) {
          chunks.push(currentChunk.trim());
          currentChunk = trimmedSentence;
        } else {
          currentChunk = currentChunk + " " + trimmedSentence;
        }
      } else {
        currentChunk = currentChunk
          ? currentChunk + " " + trimmedSentence
          : trimmedSentence;
      }

      if (currentChunk.length > maxCharsPerChunk * 1.3) {
        chunks.push(currentChunk.trim());
        currentChunk = "";
      }
    });

    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks.length > 0 ? chunks : [paragraph];
  };

  // Split content into pages based on line count (without photos)
  const splitIntoPages = (paragraphs, title) => {
    const pages = [];
    let currentPageContent = [];
    let currentLines = 3.5; // Account for title + spacing
    const charsPerLine = getCharsPerLine();
    const maxLinesPerPage = LINES_PER_PAGE - 1; // Leave 1 line buffer at bottom

    paragraphs.forEach((paragraph) => {
      // More accurate line calculation - REDUCED spacing
      const paragraphLines = Math.ceil(paragraph.length / charsPerLine);

      // Check if adding this paragraph would exceed the page limit
      if (currentLines + paragraphLines > maxLinesPerPage) {
        // Save current page if it has content
        if (currentPageContent.length > 0) {
          pages.push({
            type: "content",
            title: pages.length === 0 ? title : null,
            content: currentPageContent,
            pageBreak: false,
          });
        }
        // Start new page with this paragraph
        currentPageContent = [paragraph];
        currentLines = paragraphLines;
      } else {
        // Add paragraph to current page
        currentPageContent.push(paragraph);
        currentLines += paragraphLines;
      }
    });

    // Add remaining content
    if (currentPageContent.length > 0) {
      pages.push({
        type: "content",
        title: pages.length === 0 ? title : null,
        content: currentPageContent,
        pageBreak: true,
      });
    }

    return pages;
  };

  // Split content into pages with photos (for chapters)
  const splitIntoPagesWithPhotos = (paragraphs, title, photos) => {
    const pages = [];
    let currentPageContent = [];
    let currentLines = 3.5; // Account for title + spacing
    const charsPerLine = getCharsPerLine();
    const maxLinesPerPage = LINES_PER_PAGE - 1; // Leave 1 line buffer at bottom

    // Add photo before content if exists
    if (photos[0]) {
      currentPageContent.push({ type: "photo", url: photos[0] });
      currentLines += 7; // Photo space
    }

    paragraphs.forEach((paragraph) => {
      // More accurate line calculation - REDUCED spacing
      const paragraphLines = Math.ceil(paragraph.length / charsPerLine);

      // Check if this paragraph fits on current page
      if (currentLines + paragraphLines > maxLinesPerPage) {
        // Save current page if it has content
        if (currentPageContent.length > 0) {
          pages.push({
            type: "content",
            title: pages.length === 0 ? title : null,
            content: currentPageContent,
            pageBreak: false,
          });
        }
        // Start new page with this paragraph
        currentPageContent = [{ type: "text", text: paragraph }];
        currentLines = paragraphLines;
      } else {
        // Add paragraph to current page
        currentPageContent.push({ type: "text", text: paragraph });
        currentLines += paragraphLines;
      }
    });

    // Add photo after content if exists
    if (photos[1]) {
      const photoLines = 7;
      // Check if there's room for the photo on current page
      if (currentLines + photoLines > maxLinesPerPage) {
        // Need new page for the photo
        if (currentPageContent.length > 0) {
          pages.push({
            type: "content",
            title: pages.length === 0 ? title : null,
            content: currentPageContent,
            pageBreak: false,
          });
        }
        currentPageContent = [{ type: "photo", url: photos[1] }];
        currentLines = photoLines;
      } else {
        currentPageContent.push({ type: "photo", url: photos[1] });
        currentLines += photoLines;
      }
    }

    // Add remaining content
    if (currentPageContent.length > 0) {
      pages.push({
        type: "content",
        title: pages.length === 0 ? title : null,
        content: currentPageContent,
        pageBreak: true,
      });
    }

    return pages;
  };

  // Function to split content into pages
  const paginateContent = () => {
    const pages = [];
    const tocEntries = []; // Track TOC entries with their page numbers

    // Cover page with photo
    pages.push({
      type: "cover",
      content: bookTitle,
      photo: coverPhoto,
      pageBreak: true,
    });

    // Chapters - only first chapter if not paid
    const chaptersToShow = isPaid ? chapters : chapters.slice(0, 1);

    // Work out how many TOC entries the book will have up front so we can
    // reserve the right number of TOC pages. Reserving them now keeps the
    // printed page numbers in the TOC accurate even when it spans pages.
    const hasIntro = introduction && introduction.trim() !== " ";
    const hasSummary = isPaid && summary && summary.trim() !== " ";
    const tocEntryCount =
      (hasIntro ? 1 : 0) + chaptersToShow.length + (hasSummary ? 1 : 0);
    const tocPageCount = Math.max(
      1,
      Math.ceil(tocEntryCount / ENTRIES_PER_TOC_PAGE)
    );

    // Placeholder pages for the TOC (filled in once entries are collected)
    const tocStartIndex = pages.length;
    for (let i = 0; i < tocPageCount; i++) {
      pages.push({
        type: "toc",
        content: [],
        pageBreak: true,
      });
    }

    // Introduction - split long paragraphs
    if (hasIntro) {
      const introStartPage = pages.length + 1; // +1 for display (1-indexed)
      tocEntries.push({ title: t("introduction"), page: introStartPage });

      const introLines = introduction.split("\n").filter((p) => p.trim());
      const splitIntro = [];
      introLines.forEach((para) => {
        const chunks = splitLongParagraph(para, getCharsPerLine());
        splitIntro.push(...chunks);
      });
      const introPages = splitIntoPages(splitIntro, t("introduction"));
      pages.push(...introPages);
    }

    chaptersToShow.forEach((chapter, index) => {
      const chapterStartPage = pages.length + 1; // +1 for display (1-indexed)
      const chapterName =
        chapter.name && String(chapter.name).trim() !== "null"
          ? String(chapter.name).trim()
          : "";
      const chapterTitle = chapterName
        ? `${t("chapter")} ${index + 1}: ${chapterName}`
        : `${t("chapter")} ${index + 1}`;
      tocEntries.push({ title: chapterTitle, page: chapterStartPage });

      let chapterContent = [];

      if (chapter.generated_content_json) {
        try {
          const content =
            typeof chapter.generated_content_json === "string"
              ? JSON.parse(chapter.generated_content_json)
              : chapter.generated_content_json;
          chapterContent = content.map((item) => item.text);
        } catch (e) {
          chapterContent = [chapter.generated_content_json];
        }
      } else if (chapter.generated_content) {
        chapterContent = chapter.generated_content
          .split("\n")
          .filter((p) => p.trim());
      } else {
        chapterContent = [t("chapter_content_not_generated_yet")];
      }

      // Split long paragraphs in chapter content
      const splitChapterContent = [];
      chapterContent.forEach((para) => {
        const chunks = splitLongParagraph(para, getCharsPerLine());
        splitChapterContent.push(...chunks);
      });

      const photos = chapterPhotos[chapter.id] || {};
      const chapterPages = splitIntoPagesWithPhotos(
        splitChapterContent,
        chapterTitle,
        photos
      );
      pages.push(...chapterPages);
    });

    // Add "locked" notice if not paid and there are more chapters
    if (!isPaid && chapters.length > 1) {
      pages.push({
        type: "locked",
        content: t("unlock_full_book"),
        remainingChapters: chapters.length - 1,
        pageBreak: true,
      });
    }

    // Summary (back cover) - only show if paid, split long paragraphs
    if (hasSummary) {
      const summaryStartPage = pages.length + 1; // +1 for display (1-indexed)
      tocEntries.push({ title: t("summary"), page: summaryStartPage });

      const summaryLines = summary.split("\n").filter((p) => p.trim());
      const splitSummary = [];
      summaryLines.forEach((para) => {
        const chunks = splitLongParagraph(para, getCharsPerLine());
        splitSummary.push(...chunks);
      });
      const summaryPages = splitIntoPages(splitSummary, t("summary"));
      pages.push(...summaryPages);
    }

    // Update reserved TOC pages with their slice of entries
    for (let i = 0; i < tocPageCount; i++) {
      const start = i * ENTRIES_PER_TOC_PAGE;
      pages[tocStartIndex + i] = {
        type: "toc",
        content: tocEntries.slice(start, start + ENTRIES_PER_TOC_PAGE),
        pageBreak: true,
      };
    }

    return pages;
  };

  const [pages, setPages] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const paginatedPages = paginateContent();
      setPages(paginatedPages);
      setTotalPages(paginatedPages.length);
      setCurrentPage(1);
    }
  }, [
    isOpen,
    bookTitle,
    introduction,
    // synopsis,
    summary,
    chapters,
    coverPhoto,
    chapterPhotos,
    isPaid,
  ]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      if (contentRef.current) {
        const pageElement = contentRef.current.querySelector(
          `[data-page="${newPage}"]`
        );
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="book-preview-overlay">
      <div className="book-preview-modal-new">
        <div className="preview-header-new">
          <h3 className="preview-title-new">{t("book_preview")}</h3>
          <button className="preview-close-btn-new" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="preview-content-new" ref={contentRef}>
          <div className="a5-book-container">
            {pages.map((page, index) => (
              <div
                key={index}
                data-page={index + 1}
                className={`a5-page ${
                  page.type === "cover" ? "cover-page-new" : ""
                } ${page.type === "locked" ? "locked-page-new" : ""} ${
                  currentPage === index + 1 ? "active-page" : ""
                }`}
              >
                {page.type === "cover" ? (
                  <div className="cover-content-new">
                    <h1 className="book-title-cover">{page.content}</h1>
                    {page.photo && (
                      <div className="cover-photo">
                        <img src={page.photo} alt="Book cover" />
                      </div>
                    )}
                  </div>
                ) : page.type === "toc" ? (
                  <div className="toc-content">
                    <h2 className="toc-title">{t("table_of_contents")}</h2>
                    <div className="toc-entries">
                      {page.content.map((entry, entryIndex) => (
                        <div
                          key={entryIndex}
                          className="toc-entry"
                          onClick={() => handlePageChange(entry.page)}
                        >
                          <span className="toc-entry-title">{entry.title}</span>
                          <span className="toc-entry-dots"></span>
                          <span className="toc-entry-page">{entry.page}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : page.type === "locked" ? (
                  <div className="locked-content-new">
                    <div className="lock-icon">
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <h2 className="locked-title">{page.content}</h2>
                    <p className="locked-subtitle">
                      {page.remainingChapters} {t("more_chapters_available")}
                    </p>
                  </div>
                ) : (
                  <div className="page-content-new">
                    {page.title && (
                      <h2 className="section-title-new">{page.title}</h2>
                    )}
                    {page.content.map((item, pIndex) => (
                      <React.Fragment key={pIndex}>
                        {item.type === "photo" ? (
                          <div className="chapter-photo">
                            <img src={item.url} alt="Chapter illustration" />
                          </div>
                        ) : typeof item === "string" ? (
                          <p
                            className="paragraph-new"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeStyleTags(item),
                            }}
                          />
                        ) : (
                          <p
                            className="paragraph-new"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeStyleTags(item.text),
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <div className="page-number">{index + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPreviewModal;
